<template>
  <div class="pa-0">
    <slot name="dialog-activator" :openDialog="onDialogOpen">
      <app-tooltip v-if="!isViperSource(demographics)" bottom>
        <template #activator="{ on }">
          <v-icon class="disabled-color material-icons-round" v-on="on">edit</v-icon>
        </template>
        <span>Data source is not editable</span>
      </app-tooltip>
      <app-icon
        v-else-if="isViperSource(demographics) && hasAnyPermission('PATIENT_UPDATE')"
        data-e2e-id="patient-demographics-edit"
        @click.stop="onDialogOpen"
      >
        edit
      </app-icon>
    </slot>
    <v-dialog v-model="dialog" width="720px" persistent scrollable class="text-center">
      <v-card data-e2e-id="patient-demographics-dialog" flat>
        <v-card-title>
          <span>{{ title }}</span>
        </v-card-title>
        <v-divider />

        <v-card-text class="card-text-height px-3">
          <v-form ref="form" v-model="formValid">
            <v-text-field
              v-model.trim="form.demographics.firstName"
              label="First Name*"
              :rules="rules.required"
              clearable
            />
            <v-text-field v-model="form.demographics.middleName" label="Middle Name" clearable />
            <v-text-field
              v-model.trim="form.demographics.lastName"
              label="Last Name*"
              :rules="rules.required"
              clearable
            />
            <v-text-field
              v-model.trim="form.patient.externalId"
              label="Patient ID*"
              :rules="rules.externalId"
              :disabled="isMrnDisabled"
              clearable
            />
            <app-date-picker
              ref="dob"
              :model="form.demographics.dateOfBirth"
              :schema="dateOfBirthSchema"
              :max-date="today"
              :rules="rules.required"
              data-e2e-id="patient-demographics-dob"
              @change="onDateOfBirthChange($event)"
              @keydown.native.tab="dateOfBirthTab"
              @cleared="onDateOfBirthChange({ value: null, valid: true })"
            />
            <app-date-picker
              v-if="!dateOfDeathUnknown"
              ref="dod"
              :model="form.demographics.dateOfDeath"
              :schema="dateOfDeathSchema"
              :min-date="minDeathDate"
              :max-date="today"
              data-e2e-id="patient-demographics-dod"
              @change="onDateOfDeathChange($event)"
              @keydown.native.tab="dateOfDeathTab"
              @cleared="onDateOfDeathChange({ value: null, valid: true })"
            />
            <v-checkbox
              v-model="dateOfDeathUnknown"
              label="Date of Death Unknown"
              @change="dateOfDeathUnknownChanged"
            />
            <v-autocomplete
              v-model="form.demographics.race"
              label="Race"
              :items="races"
              item-text="value"
              item-value="id"
              return-object
              data-e2e-id="patient-demographics-race"
              clearable
            />
            <v-text-field
              v-if="showOtherField('race')"
              v-model="form.demographics.raceOther"
              label="Race - Other"
              clearable
              data-e2e-id="patient-demographics-race-other"
            />
            <v-autocomplete
              v-model="form.demographics.ethnicity"
              label="Ethnicity"
              :items="ethnicities"
              item-text="value"
              item-value="id"
              return-object
              data-e2e-id="patient-demographics-ethnicity"
              clearable
            />
            <v-autocomplete
              v-model="form.demographics.gender"
              label="Sex*"
              :items="genders"
              item-text="value"
              item-value="id"
              return-object
              data-e2e-id="patient-demographics-sex"
              clearable
              :rules="rules.required"
            />
            <v-text-field
              v-if="showOtherField('gender')"
              v-model="form.demographics.genderOther"
              label="Sex - Other"
              data-e2e-id="patient-demographics-sex-other"
              clearable
            />
          </v-form>
        </v-card-text>
        <v-divider />

        <slot name="errors"></slot>
        <v-card-actions class="pa-3">
          <v-spacer />
          <app-button-cancel data-e2e-id="patient-demographics-cancel" @click="onCancel" />
          <app-button
            :disabled="!isFormValid"
            data-e2e-id="patient-demographics-save"
            @click="onSave"
            >Save</app-button
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { permissionsMixin } from '@/mixins/permissions'
import { mapActions, mapState } from 'vuex'
import {
  CREATE_PATIENT_DEMOGRAPHICS,
  GET_DEMOGRAPHICS,
  UPDATE_PATIENT_DEMOGRAPHICS
} from '@/store/mutation-types'
import { patientService } from '@/services/patient-service'
import { MyActiveOrganization } from '@/helpers/my-account-helper'
import moment from 'moment'
import { YYYY_MM_DD } from '@/helpers/date-helper'
import _ from 'lodash'

export default {
  mixins: [permissionsMixin],
  props: {
    patient: { type: Object, default: () => {} },
    demographics: { type: Object, default: () => {} },
    title: { type: String, required: true },
    isMrnDisabled: { type: Boolean, default: false },
    enforceRequiredFields: { type: Boolean, default: true }
  },
  data() {
    return {
      dialog: false,
      form: {
        patient: {},
        demographics: {}
      },
      dateOfDeathUnknown: false,
      dateOfBirthSchema: {
        label: 'Date of Birth*',
        required: true
      },
      dateOfDeathSchema: {
        label: 'Date of Death'
      },
      formValid: false,
      minDeathDate: null,
      patientsForExternalId: [],
      patientsForFirstLastDob: []
    }
  },
  computed: {
    ...mapState({
      races: state => _.sortBy(_.map(state.races), 'value'),
      ethnicities: state => _.sortBy(_.map(state.ethnicities), 'value'),
      genders: state => _.sortBy(_.map(state.genders), 'value')
    }),
    orgType() {
      return MyActiveOrganization.type
    },
    today() {
      return moment()
        .endOf('day')
        .format()
    },
    isFormValid() {
      if (!this.enforceRequiredFields) {
        return (
          _.get(this.form.demographics, 'firstName', null) !== null ||
          _.get(this.form.demographics, 'lastName', null) !== null ||
          _.get(this.form.demographics, 'middleName', null) !== null ||
          _.get(this.form.demographics, 'dateOfBirth', null) !== null ||
          _.get(this.form.demographics, 'dateOfDeath', null) !== null ||
          !!_.get(this.form.demographics, 'deceased', false) ||
          _.get(this.form.demographics, 'race', null) !== null ||
          _.get(this.form.demographics, 'ethnicity', null) !== null ||
          _.get(this.form.demographics, 'gender', null) !== null
        )
      }
      return this.formValid
    },
    rules() {
      return {
        required: [
          v =>
            !!v || this.orgType === 'RESEARCH' || !this.enforceRequiredFields || 'Field is required'
        ],
        externalId: [
          v => this.uniqueExternalId(v) || 'Patient ID must be unique',
          v => !!v || 'Field is required'
        ]
      }
    }
  },
  watch: {
    form: {
      handler: _.debounce(function() {
        this.validateFirstLastDob()
      }, 300),
      deep: true
    }
  },
  mounted() {
    this.GET_DEMOGRAPHICS()
  },
  methods: {
    ...mapActions([GET_DEMOGRAPHICS, CREATE_PATIENT_DEMOGRAPHICS, UPDATE_PATIENT_DEMOGRAPHICS]),
    async validateFirstLastDob() {
      const firstName = _.get(this.form.demographics, 'firstName', null)
      const lastName = _.get(this.form.demographics, 'lastName', null)
      const dateOfBirth = _.get(this.form.demographics, 'dateOfBirth', null)
      if (firstName !== null && lastName !== null && dateOfBirth !== null) {
        await this.findPatientsWithDupeFirstLastDob({
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          dateOfBirth: moment(dateOfBirth).format(YYYY_MM_DD)
        })
        const duplicatePatientsFound = this.patientsForFirstLastDob.map(duplicatePatient => ({
          ...duplicatePatient,
          firstName,
          lastName
        }))
        this.$emit('duplicatePatientsFound', duplicatePatientsFound)
      } else {
        this.$emit('duplicatePatientsFound', [])
      }
    },
    onCancel() {
      this.form = {
        patient: {},
        demographics: {}
      }
      this.$refs.form.resetValidation()
      this.dialog = false
    },
    async findPatientsWithExternalId(params) {
      const response = await patientService.get(MyActiveOrganization.id, params)
      this.patientsForExternalId = response.data.content
    },
    async findPatientsWithDupeFirstLastDob(params) {
      const response = await patientService.get(MyActiveOrganization.id, params)
      this.patientsForFirstLastDob = response.data.content
    },
    uniqueExternalId(value) {
      if (this.isMrnDisabled) {
        return true
      }
      this.findPatientsWithExternalId({ externalId: value })
      const patientByExternalId = _.find(this.patientsForExternalId, it => it.externalId === value)
      return !patientByExternalId || patientByExternalId.id === this.form.patient.externalId
    },
    async onSave() {
      if (!this.isOtherDemographicsValue('race')) {
        this.form.demographics.raceOther = null
      }
      if (!this.isOtherDemographicsValue('gender')) {
        this.form.demographics.genderOther = null
      }
      this.$emit('save', this.form)
      this.onCancel()
    },
    onDateOfBirthChange(event) {
      this.setDemographicsDateValue('dateOfBirth', event)
      this.setMinDeathDate()
    },
    onDateOfDeathChange(event) {
      this.setDemographicsDateValue('dateOfDeath', event)
      this.form.demographics.deceased = !_.isNil(event.value) || this.dateOfDeathUnknown
    },
    dateOfDeathUnknownChanged() {
      if (this.dateOfDeathUnknown) {
        this.onDateOfDeathChange({ value: null, valid: true })
      } else {
        this.form.demographics.deceased = !_.isNil(this.form.demographics.dateOfDeath)
      }
      // This is to force a re computation of `isFormValid`
      this.form.demographics = _.clone(this.form.demographics)
    },
    showOtherField(fieldName) {
      return this.isOtherDemographicsValue(fieldName)
    },
    isOtherDemographicsValue(fieldName) {
      return _.get(this.form.demographics[fieldName], 'value', '').includes('Other')
    },
    onDialogOpen() {
      this.dialog = true
      this.form = { demographics: _.clone(this.demographics), patient: _.clone(this.patient) }
      this.setMinDeathDate()
      this.dateOfDeathUnknown =
        _.isNil(this.form.demographics.dateOfDeath) && this.form.demographics.deceased
    },
    setDemographicsDateValue(fieldName, event) {
      if (event.valid) {
        this.form.demographics[fieldName] = event.value
      } else {
        this.form.demographics[fieldName] = null
      }
      // This is to force a re computation of `isFormValid`
      this.form.demographics = _.clone(this.form.demographics)
    },
    setMinDeathDate() {
      this.minDeathDate = this.form.demographics.dateOfBirth
    },
    dateOfBirthTab() {
      setTimeout(() => {
        this.$refs.dob.datePicker = false
      }, 100)
    },
    dateOfDeathTab() {
      setTimeout(() => {
        this.$refs.dod.datePicker = false
      }, 100)
    },
    isViperSource(demographics) {
      return _.get(demographics, 'source.type', null) === 'VIPER'
    }
  }
}
</script>
